import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Check, X, Crown, Sparkles, ArrowRight, Shield, Zap, Video, Mic, Layers } from 'lucide-react';
import SEOHead from '../components/SEOHead';

const MARKETPLACE_LINK = 'https://buy.stripe.com/cNidRbfENgRR113fSk8AE00';
const CREATOR_LINK = 'https://buy.stripe.com/eVq7sN3W51WX5hj0Xq8AE01';
const STUDIO_LINK = 'https://buy.stripe.com/9B600l2S1dFF9xz49C8AE02';

const tiers = [
  {
    id: 'marketplace',
    name: 'Marketplace',
    price: '29',
    tagline: 'Browse, sell, and connect',
    paymentLink: MARKETPLACE_LINK,
    icon: Crown,
    color: 'amber',
    btnClass: 'bg-obsidian hover:bg-amber-600 text-white',
    features: {
      community: true, marketplace: true, sell: true, profile: true, dashboard: true,
      ai_products: false, ai_images: false, ai_video: false, ai_voice: false, bulk: false, automation: false, priority: false,
    },
  },
  {
    id: 'creator',
    name: 'Creator AI',
    price: '49',
    tagline: 'AI tools for digital products',
    paymentLink: CREATOR_LINK,
    icon: Sparkles,
    color: 'violet',
    badge: 'Popular',
    btnClass: 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700 text-white shadow-lg',
    features: {
      community: true, marketplace: true, sell: true, profile: true, dashboard: true,
      ai_products: true, ai_images: true, ai_video: false, ai_voice: false, bulk: false, automation: false, priority: false,
    },
    limits: '3 images/day · 1 planner/day · 1 toolkit/day',
  },
  {
    id: 'studio',
    name: 'Full AI Studio',
    price: '100',
    tagline: 'Unlimited creative power',
    paymentLink: STUDIO_LINK,
    icon: Zap,
    color: 'rose',
    badge: 'Pro',
    btnClass: 'bg-gradient-to-r from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 text-white shadow-lg',
    features: {
      community: true, marketplace: true, sell: true, profile: true, dashboard: true,
      ai_products: true, ai_images: true, ai_video: true, ai_voice: true, bulk: true, automation: true, priority: true,
    },
    limits: '10 images/day · 2 videos/day · 5 planners/day',
  },
];

const featureRows = [
  { key: 'community', label: 'Community & Messaging' },
  { key: 'marketplace', label: 'Marketplace Access' },
  { key: 'sell', label: 'Sell Products' },
  { key: 'profile', label: 'Creator Profile & Social Links' },
  { key: 'dashboard', label: 'Business Dashboard' },
  { key: 'ai_products', label: 'AI Product Creator (ebooks, planners, toolkits)' },
  { key: 'ai_images', label: 'AI Image Generation (covers, mockups, wallpapers)' },
  { key: 'ai_video', label: 'AI Video Generation' },
  { key: 'ai_voice', label: 'AI Audio / Voice Studio' },
  { key: 'bulk', label: 'Bulk Product Generation' },
  { key: 'automation', label: 'Advanced Automation Tools' },
  { key: 'priority', label: 'Priority Creator Processing' },
];

export default function PricingPage() {
  const { user } = useAuth();
  const currentPlan = user?.subscription_plan;
  const isActive = user?.subscription_status === 'active';

  const buildPayUrl = (link) => {
    if (!link || !user) return null;
    const p = new URLSearchParams();
    if (user.id) p.set('client_reference_id', user.id);
    if (user.email) p.set('prefilled_email', user.email);
    return `${link}?${p.toString()}`;
  };

  const isCurrent = (tierId) => {
    if (!isActive) return false;
    if (currentPlan === tierId) return true;
    if (tierId === 'marketplace' && currentPlan === 'monthly') return true;
    return false;
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <SEOHead
        title="Pricing"
        description="Susan David Creator Platform — three membership tiers. Marketplace €29/mo, Creator AI €49/mo, Full AI Studio €100/mo."
        url={`${window.location.origin}/pricing`}
      />

      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/90 border-b border-amber-100/40">
        <div className="max-w-6xl mx-auto flex items-center justify-between h-14 px-4 md:px-8">
          <Link to="/" className="font-serif text-base tracking-tight">
            <span className="text-obsidian">Susan David</span>
            <span className="text-amber-500 ml-1.5 font-light">Creator Platform</span>
          </Link>
          <Link to={user ? '/feed' : '/auth'}>
            <Button className="rounded-none text-[10px] uppercase tracking-[0.15em] h-9 px-6 bg-obsidian hover:bg-amber-600 text-white transition-colors">
              {user ? 'Dashboard' : 'Get Started'}
            </Button>
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="py-16 md:py-24 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <div className="inline-flex items-center gap-2 border border-amber-200/60 bg-amber-50/40 px-4 py-1.5 mb-8">
            <div className="w-1.5 h-1.5 bg-amber-400 rounded-full" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-amber-700 font-medium">Membership Plans</span>
          </div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-obsidian leading-tight mb-4" data-testid="pricing-headline">
            Choose your creative level
          </h1>
          <p className="text-sm text-obsidian/50 max-w-lg mx-auto font-light">
            Every plan includes 30 days free. No credit card required to start. AI tools require Creator AI or higher.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 pb-16" data-testid="pricing-plans">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {tiers.map((tier) => {
            const Icon = tier.icon;
            const current = isCurrent(tier.id);
            const payUrl = buildPayUrl(tier.paymentLink);
            const colorMap = { amber: 'border-amber-200', violet: 'border-violet-200', rose: 'border-rose-200' };
            const iconColor = { amber: 'text-amber-600', violet: 'text-violet-600', rose: 'text-rose-600' };
            const checkColor = { amber: 'text-amber-500', violet: 'text-violet-500', rose: 'text-rose-500' };

            return (
              <div
                key={tier.id}
                className={`relative border rounded-2xl p-6 transition-all duration-300 hover:shadow-xl ${colorMap[tier.color]} ${current ? 'ring-2 ring-emerald-400' : ''} ${tier.badge ? 'shadow-lg' : ''}`}
                data-testid={`plan-${tier.id}`}
              >
                {tier.badge && (
                  <div className={`absolute -top-3 right-5 text-white text-[9px] uppercase tracking-wider font-bold px-3 py-1 rounded-full ${tier.color === 'violet' ? 'bg-violet-600' : 'bg-rose-600'}`}>
                    {tier.badge}
                  </div>
                )}
                {current && (
                  <div className="absolute -top-3 left-5 bg-emerald-500 text-white text-[9px] uppercase tracking-wider font-bold px-3 py-1 rounded-full">
                    Current
                  </div>
                )}

                <Icon size={20} className={`${iconColor[tier.color]} mb-3`} />
                <h3 className="font-serif text-lg mb-0.5">{tier.name}</h3>
                <p className="text-[11px] text-muted-foreground mb-4">{tier.tagline}</p>

                <div className="flex items-baseline gap-1 mb-1">
                  <span className="text-3xl font-serif text-obsidian">{'\u20AC'}{tier.price}</span>
                  <span className="text-xs text-muted-foreground">/month</span>
                </div>
                {tier.limits && (
                  <p className="text-[10px] text-muted-foreground mb-5 border-b border-border/30 pb-4">{tier.limits}</p>
                )}
                {!tier.limits && <div className="mb-5 border-b border-border/30 pb-4" />}

                <ul className="space-y-2 mb-6">
                  {featureRows.map((f) => {
                    const has = tier.features[f.key];
                    return (
                      <li key={f.key} className={`flex items-center gap-2 text-[11px] ${has ? 'text-obsidian/80' : 'text-muted-foreground/40'}`}>
                        {has ? <Check size={13} className={checkColor[tier.color]} /> : <X size={13} className="text-muted-foreground/25" />}
                        <span className={!has ? 'line-through' : ''}>{f.label}</span>
                      </li>
                    );
                  })}
                </ul>

                {current ? (
                  <div className="text-center text-[11px] text-emerald-700 bg-emerald-50 rounded-lg py-2.5 font-medium">
                    <Check size={13} className="inline mr-1" /> Your current plan
                  </div>
                ) : payUrl ? (
                  <a href={payUrl} target="_blank" rel="noopener noreferrer">
                    <Button data-testid={`plan-${tier.id}-cta`} className={`w-full rounded-lg h-11 text-xs uppercase tracking-wider transition-all duration-300 ${tier.btnClass}`}>
                      Subscribe <ArrowRight size={14} className="ml-1.5" />
                    </Button>
                  </a>
                ) : !user ? (
                  <Link to="/auth">
                    <Button data-testid={`plan-${tier.id}-cta`} className="w-full rounded-lg h-11 text-xs uppercase tracking-wider bg-obsidian hover:bg-amber-600 text-white transition-all">
                      Start Free Trial <ArrowRight size={14} className="ml-1.5" />
                    </Button>
                  </Link>
                ) : (
                  <Button data-testid={`plan-${tier.id}-cta`} disabled className="w-full rounded-lg h-11 text-xs bg-stone-100 text-stone-400 cursor-not-allowed">
                    Coming Soon
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* Feature Comparison Table */}
        <div className="mt-16" data-testid="feature-comparison">
          <h3 className="font-serif text-xl text-obsidian text-center mb-6">Compare All Features</h3>
          <div className="bg-white border border-border/40 rounded-2xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/40 bg-stone-50/50">
                  <th className="text-left px-5 py-3 text-[10px] uppercase tracking-wider text-muted-foreground font-medium w-[40%]">Feature</th>
                  <th className="text-center px-3 py-3 text-[10px] uppercase tracking-wider text-amber-700 font-medium">Marketplace<br/><span className="text-muted-foreground">{'\u20AC'}29/mo</span></th>
                  <th className="text-center px-3 py-3 text-[10px] uppercase tracking-wider text-violet-700 font-medium">Creator AI<br/><span className="text-muted-foreground">{'\u20AC'}49/mo</span></th>
                  <th className="text-center px-3 py-3 text-[10px] uppercase tracking-wider text-rose-700 font-medium">AI Studio<br/><span className="text-muted-foreground">{'\u20AC'}100/mo</span></th>
                </tr>
              </thead>
              <tbody>
                {featureRows.map((f, i) => (
                  <tr key={f.key} className="border-b border-border/15 last:border-0">
                    <td className="px-5 py-2.5 text-xs text-obsidian/70">{f.label}</td>
                    {tiers.map(t => (
                      <td key={t.id} className="text-center px-3 py-2.5">
                        {t.features[f.key]
                          ? <Check size={15} className={`inline ${t.color === 'amber' ? 'text-amber-500' : t.color === 'violet' ? 'text-violet-500' : 'text-rose-500'}`} />
                          : <X size={15} className="inline text-muted-foreground/20" />
                        }
                      </td>
                    ))}
                  </tr>
                ))}
                {/* Daily limits row */}
                <tr className="border-t border-border/30 bg-stone-50/30">
                  <td className="px-5 py-2.5 text-xs text-obsidian/70 font-medium">Daily AI Limits</td>
                  <td className="text-center px-3 py-2.5 text-[10px] text-muted-foreground">—</td>
                  <td className="text-center px-3 py-2.5 text-[10px] text-violet-600">3 img · 1 plan · 1 kit</td>
                  <td className="text-center px-3 py-2.5 text-[10px] text-rose-600">10 img · 2 vid · 5 plan</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Trial Info */}
        <div className="mt-12 text-center bg-stone-50 rounded-2xl p-8 border border-border/30" data-testid="trial-info">
          <Shield size={28} className="mx-auto text-amber-500 mb-3" />
          <h3 className="font-serif text-lg text-obsidian mb-2">30 days free to explore</h3>
          <p className="text-xs text-muted-foreground max-w-md mx-auto mb-4 font-light">
            New members get 1 month free to browse the community and marketplace. AI tools require a Creator AI or Studio subscription. No credit card needed to start.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-5 text-[10px] text-obsidian/40 uppercase tracking-wider">
            <span className="flex items-center gap-1.5"><Check size={12} className="text-emerald-500" /> No card required</span>
            <span className="flex items-center gap-1.5"><Check size={12} className="text-emerald-500" /> Cancel anytime</span>
            <span className="flex items-center gap-1.5"><Check size={12} className="text-emerald-500" /> Upgrade or downgrade freely</span>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-amber-100/40 py-8 bg-white">
        <div className="max-w-6xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[10px] text-muted-foreground">Susan David Creator Platform</p>
          <div className="flex items-center gap-5">
            <Link to="/marketplace" className="text-[10px] text-muted-foreground hover:text-amber-600 transition-colors">Marketplace</Link>
            <Link to="/creators" className="text-[10px] text-muted-foreground hover:text-amber-600 transition-colors">Creators</Link>
            <Link to="/auth" className="text-[10px] text-muted-foreground hover:text-amber-600 transition-colors">Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
